import * as React from "react";
import { Text, View } from "react-native";
import * as Clipboard from "expo-clipboard";
import { debounce, isEmpty } from "lodash";
import { Controller } from "react-hook-form";
import { useQuery } from "react-query";

import { useSaveFormContext } from "~/components/SaveForm/shared";
import { useGlobalHonoClient } from "~/hooks/HonoContext";
import { TextInput } from "~/ui/TextInput";

export function URLInput() {
  const { control, getFieldState, setValue, getValues } = useSaveFormContext();
  const { globalHc } = useGlobalHonoClient();

  const [url, setURL] = React.useState(getValues("url") ?? "");
  const debouncedSetURL = React.useMemo(() => debounce(setURL, 500), []);
  const step = getValues("step");

  useQuery({
    queryKey: ["scrape", url],
    queryFn: async () => {
      return globalHc["scrape"]
        .$post({
          json: {
            url: url,
          },
        })
        .then((res) => res.json());
    },
    onSuccess: (data) => {
      if (isEmpty(data.data)) return;

      if (!getFieldState("title").isTouched && data?.data?.title) {
        setValue("title", data.data.title);
      }

      if (!getFieldState("description").isTouched && data?.data?.description) {
        setValue("description", data.data.description);
      }

      setValue("urlMeta", data.data);
    },
    onError: (error) => {
      console.error(error);
    },
    enabled:
      (getValues("step") === "addUrl" || getValues("step") === "editUrl") &&
      URL.canParse(url),
    staleTime: 1000 * 60 * 5, // 5 minutes
  });

  const shouldSetValueFromClipboard = (() => {
    const urlFieldState = getFieldState("url");

    if (step !== "addUrl") return false;
    if (url) return false;
    if (urlFieldState.isDirty) return false;
    if (urlFieldState.isTouched) return false;

    return true;
  })();

  React.useEffect(() => {
    if (!shouldSetValueFromClipboard) return;

    Clipboard.getStringAsync().then((text) => {
      try {
        const clipboardURL = new URL(text).toString();

        setValue("url", clipboardURL);
        debouncedSetURL(clipboardURL);
      } catch (e) {}
    });
  }, [debouncedSetURL, setValue, shouldSetValueFromClipboard]);

  return (
    <Controller
      control={control}
      name="url"
      render={({ field, fieldState }) => (
        <View className="gap-y-1">
          <TextInput
            onChangeText={(v) => {
              field.onChange(v);
              debouncedSetURL(v);
            }}
            onBlur={field.onBlur}
            value={field.value ?? ""}
            autoFocus
            placeholder="Paste a link here"
            errorMessage={fieldState.error?.message}
            inputMode="url"
            autoCapitalize="none"
            dataSet={{
              autofocus: "true",
            }}
          />
          {(() => {
            try {
              const url = new URL(field.value);
              return (
                <View className="flex-row">
                  <Text className="text-white">Link for </Text>
                  <Text className="text-text-brand">
                    {url.hostname.replace("www.", "")}
                  </Text>
                </View>
              );
            } catch (e) {}
          })()}
        </View>
      )}
    />
  );
}
